import { Injectable } from '@angular/core';

import {
    ChartTemplateElementDto,
    ConsumptionTableTemplateElementAggregatedDto,
    HeaderElement,
    HeaderTemplateElementDto,
    KpiSetElement,
    KpiSetsElementDto,
    LineChartElement,
    ScatterPlotChartElement,
    ScatterPlotElementDto,
    SectionTemplateDto,
    TableElement,
    TableTemplateElementAggregatedDto,
    TemplateSection,
} from '../model';
import { ConsumptionTableElement } from '../model/consumption-table-element.model';

@Injectable()
export class TemplateToDtoHelperService {
    /**
     * Format template section from ui to template sections according dto layout
     * @param {TemplateSection[]} sections : sections which are rendered in ui
     */
    formatSectionsDto(sections: TemplateSection[]): SectionTemplateDto[] {
        const templateSection: TemplateSection[] = [
            {
                id: sections[0]?.id,
                sortId: sections[0]?.sortId,
                title: '',
                elements: [],
            },
        ];

        sections.forEach((section: TemplateSection) => {
            templateSection[0].elements.push(...section['elements']);
        });
        return templateSection.map((section: TemplateSection) => this.formatSectionTemplateDto(section));
    }

    /**
     * Format template section from ui to template section accourding dto
     * @param {TemplateSection} section : template section which is rendered in ui
     */
    private formatSectionTemplateDto(section: TemplateSection): SectionTemplateDto {
        const sectionTemplateDto: SectionTemplateDto = {
            id: section.id,
            sortId: section.sortId,
            title: section.title,
            headers: [],
            charts: [],
            scatterPlotCharts: [],
            tables: [],
            consumptionTables: [],
            kpiSets: [],
            sorting: [],
        };

        section.elements.forEach((element, sortId: number) => {
            sectionTemplateDto.sorting.push({
                elementId: element.id,
                sortId,
            });

            switch (element.constructor) {
                case TableElement: {
                    const tableElement = this.formatTablesDto(element as TableElement);
                    sectionTemplateDto.tables.push(tableElement);
                    break;
                }
                case ConsumptionTableElement: {
                    const consumptionTableElement = this.formatTablesDto(element as TableElement);
                    sectionTemplateDto.consumptionTables.push(consumptionTableElement);
                    break;
                }
                case LineChartElement:
                    sectionTemplateDto.charts.push(this.formatChartsDto(element as LineChartElement));
                    break;
                case ScatterPlotChartElement:
                    sectionTemplateDto.scatterPlotCharts.push(
                        this.formatScatterPlotChartsDto(element as ScatterPlotChartElement),
                    );
                    break;
                case KpiSetElement:
                    sectionTemplateDto.kpiSets.push(this.formatKpiSetsDto(element as KpiSetElement));
                    break;
                case HeaderElement:
                    sectionTemplateDto.headers.push(this.formatHeaderDto(element));
                    break;
                default:
                    break;
            }
        });
        return sectionTemplateDto;
    }

    /**
     * Format header template from ui to header template according dto
     * @param element : header element rendered in ui
     */
    private formatHeaderDto(element: HeaderElement): HeaderTemplateElementDto {
        return {
            id: element.id,
            title: element.title,
        };
    }

    /**
     * Format line chart element from ui to line chart element according dto
     * @param {LineChartElement} element : line chart element rendered in ui
     */
    private formatChartsDto(element: LineChartElement): ChartTemplateElementDto {
        return {
            id: element.id,
            title: element.title,
            datapoints: element.datapoints,
            legend: element.legend,
            description: element.description,
            kpi: element.kpi,
            inOperationFilterType: element.inOperationFilterType,
            datapointFilters: element.datapointFilters,
        };
    }

    /**
     * Format line chart element from ui to line chart element according dto
     * @param {LineChartElement} element : line chart element rendered in ui
     */
    private formatScatterPlotChartsDto(element: ScatterPlotChartElement): ScatterPlotElementDto {
        return {
            id: element.id,
            title: element.title,
            datapoints: element.datapoints,
            kpi: element.kpi,
            legend: element.legend,
            datapointFilters: element.datapointFilters,
            inOperationFilterType: element.inOperationFilterType,
            description: element.description,
        };
    }

    /**
     * Format KPI set element from UI to KPI set element according dto.
     * @param {KpiSetElement} element : KPI set element rendered in ui.
     */
    private formatKpiSetsDto(element: KpiSetElement): KpiSetsElementDto {
        return {
            id: element.id,
            name: element.title,
            linkedVisuals: element.linkedVisuals,
        };
    }

    /**
     * Format table element from ui to table element according dto
     * @param {TableElement} element : table element rendered in ui
     */ private formatTablesDto(
        element: TableElement,
    ): TableTemplateElementAggregatedDto | ConsumptionTableTemplateElementAggregatedDto {
        return {
            granularity: element.granularity,
            datapoints: element.datapoints,
            id: element.id,
            title: element.title,
            description: element.description,
        };
    }
}
